import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { formatDistanceToNow } from 'date-fns'
import { brandName } from '#app/components/branding.js'
import { publicProfileCommentFilter } from '#app/utils/comments.server.js'
import { prisma } from '#app/utils/db.server.js'
import { getUserImgSrc } from '#app/utils/misc.tsx'

export async function loader({ params }: LoaderFunctionArgs) {
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
		},
		where: { username: params.username },
	})

	invariantResponse(owner, 'Not found', { status: 404 })

	const comments = await prisma.comment.findMany({
		where: { userId: owner.id, ...publicProfileCommentFilter },
		select: {
			id: true,
			text: true,
			createdAt: true,
			script: { select: { title: true, slug: true } },
			guide: { select: { title: true, slug: true } },
			build: {
				select: {
					name: true,
					slug: true,
					server: { select: { slug: true } },
				},
			},
			newsPost: { select: { title: true, slug: true } },
			fanSite: { select: { title: true, slug: true } },
			botScriptBuild: { select: { id: true, name: true } },
		},
		orderBy: { createdAt: 'desc' },
		take: 50,
	})

	return json({ owner, comments })
}

const categoryStyles: Record<string, { label: string; className: string }> = {
	script: { label: 'Script', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
	guide: { label: 'Guide', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
	build: { label: 'Build', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' },
	news: { label: 'News', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' },
	fanSite: { label: 'Fan Site', className: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300' },
	builderBuild: { label: 'Builder Build', className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300' },
}

export default function UserCommentsRoute() {
	const { owner, comments } = useLoaderData<typeof loader>()
	const ownerDisplayName = owner.name ?? owner.username

	return (
		<div className="container mx-auto mb-24 px-4 pt-8 md:mb-48">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Link to={`/users/${owner.username}`}>
					<img
						src={getUserImgSrc(owner.image?.id)}
						alt={ownerDisplayName}
						className="h-10 w-10 rounded-full object-cover md:h-12 md:w-12"
					/>
				</Link>
				<div>
					<Link to={`/users/${owner.username}`} className="text-sm text-muted-foreground hover:underline">
						&larr; {ownerDisplayName}
					</Link>
					<h1 className="text-xl font-bold md:text-2xl">Comments ({comments.length})</h1>
				</div>
			</div>

			{/* Comments list */}
			<div className="mt-6 space-y-3">
				{comments.length === 0 && (
					<p className="text-sm text-muted-foreground">No comments yet.</p>
				)}
				{comments.map((comment) => {
					let category: string | null = null
					let title: string | undefined
					let link: string | undefined

					if (comment.script) {
						category = 'script'
						title = comment.script.title
						link = `/script/${comment.script.slug}`
					} else if (comment.guide) {
						category = 'guide'
						title = comment.guide.title
						link = `/guide/${comment.guide.slug}`
					} else if (comment.build) {
						category = 'build'
						title = comment.build.name
						link = `/builds/${comment.build.server.slug}/${comment.build.slug}`
					} else if (comment.newsPost) {
						category = 'news'
						title = comment.newsPost.title
						link = `/news/${comment.newsPost.slug}`
					} else if (comment.fanSite) {
						category = 'fanSite'
						title = comment.fanSite.title
						link = `/fan-site/${comment.fanSite.slug}`
					} else if (comment.botScriptBuild) {
						category = 'builderBuild'
						title = comment.botScriptBuild.name
						link = `/script-builder/build/${comment.botScriptBuild.id}`
					}

					if (!category || !link) return null

					const style = categoryStyles[category]!
					const timeAgo = formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })

					return (
						<div
							key={comment.id}
							className="rounded-xl border bg-muted/30 px-4 py-3"
						>
							<div className="flex items-center gap-2 text-sm">
								<span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${style.className}`}>
									{style.label}
								</span>
								<Link to={link} className="font-medium truncate hover:underline">
									{title}
								</Link>
								<span className="shrink-0 text-xs text-muted-foreground ml-auto">{timeAgo}</span>
							</div>
							<p className="mt-1.5 whitespace-pre-wrap text-sm text-muted-foreground line-clamp-3">
								{comment.text}
							</p>
						</div>
					)
				})}
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.owner.name ?? params.username
	return [
		{ title: `${displayName}'s Comments | ${brandName}` },
		{ name: 'description', content: `${displayName}'s comments on ${brandName}` },
	]
}
